import { useEffect, useState } from 'react';
import { ApiError } from '@/api/client';
import { Icon } from '@/components/Icon';
import { useFormFactor } from '@/lib/formFactor';
import { money, qty } from '@/lib/format';
import { closePurchaseOrderRemaining, deletePurchaseOrder, submitPurchaseOrderDraft } from './api';
import { poTotals } from './PurchaseCards';
import type { PurchaseOrderDetail } from './types';
import s from './PurchaseConfirmDialog.module.css';

type Action = 'close' | 'delete' | 'submit';

export function PurchaseConfirmDialog({ order, action, onClose, onDone }: {
  order: PurchaseOrderDetail;
  action: Action;
  onClose: () => void;
  onDone: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const ff = useFormFactor();
  const isClose = action === 'close';
  const isSubmit = action === 'submit';
  const total = poTotals(order.lines);
  const title = isClose ? '终止剩余入库并结案？' : isSubmit ? '确认下单 ' + order.purchaseNo + '？' : '删除采购单 ' + order.purchaseNo + '？';

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  async function confirm() {
    if (saving || uncertain) return;
    setSaving(true);
    setError('');
    try {
      if (isClose) await closePurchaseOrderRemaining(order.id);
      else if (isSubmit) await submitPurchaseOrderDraft(order.id);
      else await deletePurchaseOrder(order.id);
      onDone();
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status >= 500) setUncertain(true);
      setError(cause instanceof Error ? cause.message : '操作失败');
    } finally {
      setSaving(false);
    }
  }

  return <div className={s.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}>
    <section className={s.dialog} role="dialog" aria-modal="true" aria-label={isClose ? '终止剩余入库并结案' : isSubmit ? '确认采购草稿下单' : `删除采购单 ${order.purchaseNo}`}>
      <header className={s.head}>
        {ff !== 'desktop' && <div className={isClose || isSubmit ? s.actionIconWarn : s.actionIconDanger}><Icon name={isSubmit ? 'check' : isClose ? 'flag' : 'trash'} size={20} /></div>}
        <h2>{title}</h2>
        {ff === 'desktop' && <button type="button" onClick={onClose} disabled={saving} aria-label="关闭"><Icon name="x" size={18} /></button>}
      </header>
      <div className={s.body}>
        <div className={`${s.confirm} ${isClose || isSubmit ? s.warn : s.danger} ${ff === 'desktop' ? s.desktopConfirm : ''}`}>
          {ff === 'desktop' && <Icon name={isSubmit ? 'check' : isClose ? 'alert' : 'trash'} size={20} />}
          {isClose ? <p>采购单 <b className="tnum">{order.purchaseNo}</b> 剩余 <b>{qty(total.pending)} 个</b>将不再入库，采购单按已入库部分结案。{ff === 'desktop' && <><br /><br />已入库的 <b>{qty(total.received)} 个</b>和对应的库存流水<b>全部保留</b>。</>} 未入库部分的应付 <b className="tnum">{money(total.pendingValue || 0)}</b> 会从往来结算中扣除。</p>
            : isSubmit ? <p>确认后，草稿将变为<b>已下单</b>，并进入待到货列表。共 <b>{order.lines.length} 项</b>，采购数量 <b>{qty(total.ordered)}</b>{order.totalAmount !== undefined && <>，金额 <b>{money(order.totalAmount)}</b></>}。请先核对采购明细。</p>
              : <p>此操作不可恢复。采购单和 <b>{order.lines.length} 行明细</b>（{order.lines.map((line) => line.product.name + ' ×' + qty(line.quantity)).join('、')}）会从列表中移除，<b>操作日志会保留</b>。</p>}
        </div>
        {isClose && ff !== 'desktop' && <div className={s.preserve}><b>会保留：</b>已入库的 {qty(total.received)} 个和对应的库存流水。</div>}
        {!isClose && !isSubmit && ff !== 'desktop' && <div className={s.preserve}>{total.received === 0 ? '这张单还没有入库，可以删除。已入库或部分入库的采购单不能删除，只能「终止剩余入库」。' : '这张单已有入库，不能直接删除。请终止剩余入库并结案。'}</div>}
        {error && <p className={s.error} role="alert">{error}{uncertain && '。请求结果未确认，请先刷新采购单核对，暂勿重复提交。'}</p>}
      </div>
      <footer className={s.foot}>
        <button type="button" onClick={onClose} disabled={saving}>{isClose ? '继续等待到货' : isSubmit ? '返回修改' : ff === 'desktop' ? '取消' : '保留采购单'}</button>
        <button type="button" className={s.submit} onClick={() => void confirm()} disabled={saving || uncertain}>{saving ? '处理中…' : isClose ? '终止并结案' : isSubmit ? '确认下单' : ff === 'desktop' ? '删除' : '删除采购单'}</button>
      </footer>
    </section>
  </div>;
}
